import { useForm } from "react-hook-form";
import { useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { EnvelopeIcon } from "styles/icons";
import { useLanguage, useTranslations } from "contexts/LocalizationContext/hooks";
import config from "config/config";
import axios from "axios";
import { Chakra } from "@airhelp/react-ui-components";
import { Terms, FormSubmitButton, FormError } from "components/FormControl";
import getPageUrl from "utils/getPageUrl";
import { iterableListIds } from "../pages/api/iterable/config";

const { FormControl, Input, Stack, InputGroup, InputLeftElement, chakra } = Chakra;

interface SubscribeFormProps {
  buttonText: string;
  listId: string;
  setHasSubscribed: Dispatch<SetStateAction<boolean>>;
}

const Form = chakra("form");

const SubscribeForm = ({ setHasSubscribed, buttonText, listId }: SubscribeFormProps) => {
  const [isError, setIsError] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm();

  const watchTerms = watch("agreeWithTerms", false);
  const language = useLanguage();
  const translations = useTranslations();

  const onSubmit = ({ email }) => {
    const payload = {
      listId: iterableListIds[listId],
      email,
    };

    try {
      axios.post(`${config.websiteUrl}/api/iterable/`, payload);
      setHasSubscribed(true);
    } catch (err) {
      setIsError(true);
      setHasSubscribed(false);
    }
  };

  const termsTranslationVariables = {
    "%terms-link-attrs%": `href="${getPageUrl(
      language,
      "terms"
    )}" target="_blank" rel="noopener noreferrer" title="${translations(
      "terms_link_title"
    )}" style="text-decoration: underline"`,
    "%privacy-link-attrs%": `href="${getPageUrl(
      language,
      "privacy"
    )}" target="_blank" rel="noopener noreferrer" title="${translations(
      "terms_privacy_link_title"
    )}" style="text-decoration: underline"`,
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)} width={{ base: "full", lg: "60%" }}>
      <FormControl flexDir="column">
        <Stack direction={{ base: "column", md: "row" }} py={6} spacing={{ base: 3, md: 4 }}>
          <InputGroup w={{ base: "full", md: 80 }}>
            <InputLeftElement pointerEvents="none">
              <EnvelopeIcon color="greyscale.500" w={5} h={4} />
            </InputLeftElement>
            <Input
              name="email"
              ref={register}
              required
              type="email"
              borderRadius="md"
              placeholder={translations("enter_email_placeholder")}
            />
          </InputGroup>
          <FormSubmitButton
            text={buttonText}
            isDisabled={!watchTerms}
            isLoading={isSubmitting}
            display={{ base: "none", md: "block" }}
            w={{ base: "full", md: 40 }}
            ml={6}
          />
        </Stack>
        <Terms
          register={register({ required: true })}
          htmlContent={translations("agree_to_terms", termsTranslationVariables)}
        />
        <FormSubmitButton
          text={buttonText}
          isDisabled={!watchTerms}
          isLoading={isSubmitting}
          display={{ base: "block", md: "none" }}
          w="full"
          mt={6}
        />
        {isError && (
          <FormError
            formErrorText={translations("subscribe_form_submit_error")}
            formError={isError}
            id="form-error"
          />
        )}
      </FormControl>
    </Form>
  );
};

export default SubscribeForm;
